/**
 * JSON Patch v3 Native Wrapper
 *
 * This module provides direct access to fast-json-patch v3.1.1 functionality.
 * Uses the native v3 API and return values without modification.
 *
 * Key differences from v2:
 * - Strict RFC 6902 validation
 * - Different error objects (JsonPatchError with structured properties)
 * - May support root path operations
 * - Returns different data structures
 *
 * @see https://github.com/Starcounter-Jack/JSON-Patch
 * @see UPGRADE_GUIDE.md for migration details
 */

const { convertMongodbObjectsToStrings } = require('./convertMongodbObjectsToStrings');
const jsonpatch = require('fast-json-patch');
const cloneDeep = require('lodash.clonedeep');

/**
 * Apply a JSON Patch sequence to a document
 *
 * @param {Object} document - The document to patch (will be mutated)
 * @param {Array} patches - Array of JSON Patch operations
 * @param {boolean} validate - Whether to validate operations (default: true)
 * @param {boolean} mutateDocument - Whether to mutate the original document (default: true)
 * @param {boolean} banPrototypeModifications - Whether to ban __proto__ modifications (default: true)
 * @returns {Object} Result object with newDocument and any errors
 *
 * @example
 * const doc = { foo: 'bar' };
 * const result = apply(doc, [{ op: 'replace', path: '/foo', value: 'baz' }]);
 * // result = { newDocument: { foo: 'baz' } }
 */
function apply(document, patches, validate = true, mutateDocument = true, banPrototypeModifications = true) {
    try {
        // fast-json-patch v3 API
        const result = jsonpatch.applyPatch(document, patches, validate, mutateDocument, banPrototypeModifications);

        // v3 returns: { newDocument, removed } or throws on error
        return result;
    } catch (error) {
        // Re-throw with additional context
        error.message = `[v3] ${error.message}`;
        throw error;
    }
}

/**
 * Helper function to convert Date objects to ISO strings
 * This is needed because Date objects are not properly comparable by fast-json-patch
 * This only handles plain JavaScript Date objects, not MongoDB BSON types
 * Known issue.  See https://github.com/Starcounter-Jack/JSON-Patch/pull/330
 * @param {Object} obj - The object to convert
 * @returns {Object} New object with Date objects converted to ISO strings
 */
function convertDatesToStrings(obj) {
    if (obj == null || typeof obj !== 'object') {
        return obj;
    }

    // Skip MongoDB BSON types - they'll be handled by convertMongodbObjectsToStrings
    if (obj._bsontype) {
        return obj;
    }

    // Handle Date objects
    if (obj instanceof Date) {
        return obj.toISOString();
    }

    // Recursively process arrays
    if (Array.isArray(obj)) {
        return obj.map(item => convertDatesToStrings(item));
    }

    // Recursively process plain objects
    const result = {};
    for (const key in obj) {
        if (obj.hasOwnProperty(key)) {
            result[key] = convertDatesToStrings(obj[key]);
        }
    }
    return result;
}

/**
 * Generate a JSON Patch sequence by comparing two documents
 *
 * @param {Object} from - The original document
 * @param {Object} to - The target document
 * @param {Object} options - Options object
 * @param {boolean} options.convertMongodbObjects - Whether to convert MongoDB ObjectIDs to strings (default: true)
 * @returns {Array} Array of JSON Patch operations
 *
 * @example
 * const patch = compare({ foo: 'bar' }, { foo: 'baz' });
 * // patch = [{ op: 'replace', path: '/foo', value: 'baz' }]
 */
function compare(from, to, options = {}) {
    try {
        const { convertMongodbObjects = true } = options;

        let left = cloneDeep(from);
        let right = cloneDeep(to);

        // Always convert Date objects to ISO strings for proper comparison
        // Date objects are standard JavaScript and need to be serializable
        left = convertDatesToStrings(left);
        right = convertDatesToStrings(right);

        // Optionally convert MongoDB BSON types
        if (convertMongodbObjects) {
            left = convertMongodbObjectsToStrings(left);
            right = convertMongodbObjectsToStrings(right);
        }
        // fast-json-patch v3 API
        return jsonpatch.compare(left, right);
    } catch (error) {
        error.message = `[v3] ${error.message}`;
        throw error;
    }
}

/**
 * Validate a JSON Patch sequence
 *
 * @param {Array} patches - Array of JSON Patch operations to validate
 * @param {Object} document - Optional document to validate against
 * @returns {Object|null} JsonPatchError if invalid, null if valid
 *
 * @example
 * const error = validate([{ op: 'invalid', path: '/foo' }]);
 * // error = JsonPatchError with details
 */
function validate(patches, document) {
    try {
        // fast-json-patch v3 API
        const error = jsonpatch.validate(patches, document);
        return error || null;
    } catch (error) {
        error.message = `[v3] ${error.message}`;
        throw error;
    }
}

/**
 * Observe an object for changes and generate patches
 *
 * @param {Object} object - The object to observe
 * @returns {Object} Observer object with generate() method
 *
 * @example
 * const doc = { foo: 'bar' };
 * const observer = observe(doc);
 * doc.foo = 'baz';
 * const patches = observer.generate();
 * // patches = [{ op: 'replace', path: '/foo', value: 'baz' }]
 */
function observe(object) {
    try {
        return jsonpatch.observe(object);
    } catch (error) {
        error.message = `[v3] ${error.message}`;
        throw error;
    }
}

/**
 * Unobserve an object
 *
 * @param {Object} object - The object to stop observing
 * @param {Object} observer - The observer to remove
 */
function unobserve(object, observer) {
    try {
        return jsonpatch.unobserve(object, observer);
    } catch (error) {
        error.message = `[v3] ${error.message}`;
        throw error;
    }
}

/**
 * Generate patches from an observer
 *
 * @param {Object} observer - The observer object from observe()
 * @returns {Array} Array of patches
 *
 * @example
 * const doc = { foo: 'bar' };
 * const observer = observe(doc);
 * doc.foo = 'baz';
 * const patches = generate(observer);
 * // patches = [{ op: 'replace', path: '/foo', value: 'baz' }]
 */
function generate(observer) {
    try {
        return jsonpatch.generate(observer);
    } catch (error) {
        error.message = `[v3] ${error.message}`;
        throw error;
    }
}

/**
 * Validate a single operation
 *
 * @param {Object} operation - The operation to validate
 * @param {number} index - Index of operation in sequence
 * @param {Object} document - Optional document to validate against
 * @param {string} existingPathFragment - Optional existing path fragment
 * @throws {JsonPatchError} If operation is invalid
 *
 * @example
 * validator({ op: 'add', path: '/foo', value: 'bar' }, 0);  // No error
 * validator({ op: 'invalid' }, 0);  // Throws
 */
function validator(operation, index, document, existingPathFragment) {
    try {
        return jsonpatch.validator(operation, index, document, existingPathFragment);
    } catch (error) {
        // v3 errors already have good structure, just prefix message
        error.message = `[v3] ${error.message}`;
        throw error;
    }
}

/**
 * Deep clone an object (v3 utility)
 *
 * @param {*} obj - The object to clone
 * @returns {*} Deep cloned copy
 */
function deepClone(obj) {
    try {
        return jsonpatch.deepClone(obj);
    } catch (error) {
        error.message = `[v3] ${error.message}`;
        throw error;
    }
}

/**
 * Escape a JSON Pointer token
 *
 * @param {string} str - The string to escape
 * @returns {string} Escaped string
 */
function escapePathComponent(str) {
    try {
        return jsonpatch.escapePathComponent(str);
    } catch (error) {
        error.message = `[v3] ${error.message}`;
        throw error;
    }
}

/**
 * Unescape a JSON Pointer token
 *
 * @param {string} str - The string to unescape
 * @returns {string} Unescaped string
 */
function unescapePathComponent(str) {
    try {
        return jsonpatch.unescapePathComponent(str);
    } catch (error) {
        error.message = `[v3] ${error.message}`;
        throw error;
    }
}

// Export v3 API
module.exports = {
    apply,
    compare,
    validate,
    validator,
    observe,
    unobserve,
    generate,
    deepClone,
    escapePathComponent,
    unescapePathComponent,

    // Direct access to underlying library if needed
    _fastJsonPatch: jsonpatch
};
